import { useEffect, useRef, useState } from "react";
import { signInWithCustomToken, signOut, type User as AuthUser } from "firebase/auth";
import type { EmbedSessionConsumeResponse } from "@gull/shared";
import { auth } from "./firebase";
import { enduserApi, ApiError } from "./api";

export type EmbedSession =
  | { status: "pending" }
  | { status: "ready"; user: AuthUser }
  | { status: "expired"; code: string; message: string; serverError: boolean };

/**
 * Signs the framed customer in from TRY's `__session` cookie. The cookie is `HttpOnly`, so it is
 * never read here — it rides along on the same-origin request, and the server answers with a
 * Firebase custom token.
 */
export function useEmbedSession(): EmbedSession {
  const [session, setSession] = useState<EmbedSession>({ status: "pending" });
  const consumed = useRef(false);

  useEffect(() => {
    // One exchange and one sign-in per frame load; StrictMode mounts the effect twice in dev.
    if (consumed.current) return;
    consumed.current = true;

    void (async () => {
      try {
        const { customToken } = await enduserApi.publicPost<EmbedSessionConsumeResponse>(
          "/embed-sessions/consume",
          {},
        );
        const credential = await signInWithCustomToken(auth, customToken);
        setSession({ status: "ready", user: credential.user });
      } catch (err) {
        // A session this browser kept from an earlier visit must not outlive TRY's own.
        await signOut(auth).catch(() => undefined);
        setSession({
          status: "expired",
          code: err instanceof ApiError ? err.code : "NETWORK",
          message: err instanceof Error ? err.message : "Ukjent feil",
          serverError: err instanceof ApiError && err.status >= 500,
        });
      }
    })();
  }, []);

  return session;
}
